import React, { useState } from 'react';
import Axios from 'axios';
import Select from '@/components/common/ReactSelect5';
import { Button, SubmitButton } from '@/components/ui/Buttons';
import { Input, Label } from '@/components/ui/Form';
import Modal from '@/components/ui/Modal';
import Text from '@/components/ui/Text';
import setupCsrfToken from '@/modules/SetupCSRFToken';

const LABEL_CLASSES = 'tw-w-auto tw-font-bold tw-text-sm';

// Mirrors LeveledReadingContentRule#build_regexp closely enough for live preview: plain terms
// compile with word boundaries + optional plural, case-insensitive. The Ruby validation on save
// remains authoritative (JS and Ruby regex semantics differ at the edges).
export const buildMatcher = (term: string, pattern: string): RegExp | null => {
  try {
    if (pattern.trim()) return new RegExp(pattern, 'i');
    if (term.trim()) {
      const escaped = term.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`\\b${escaped}s?\\b`, 'i');
    }
  } catch {
    return null;
  }
  return null;
};

interface PreviewLineProps {
  expected: boolean;
  label: string;
  matcher: RegExp | null;
  text: string;
}

const PreviewLine = ({ expected, label, matcher, text }: PreviewLineProps) => {
  if (!text.trim() || !matcher) return null;

  const matches = matcher.test(text);
  const pass = matches === expected;
  return (
    <div className="tw-mt-1 tw-text-sm" style={{ color: pass ? '#0b5d0b' : '#8f1f1f' }}>
      {pass ? '✓' : '✗'} {label} {matches ? 'matches' : 'does not match'}
      {!pass && expected && ' (it must match)'}
      {!pass && !expected && ' (it must not match)'}
    </div>
  );
};

interface FieldProps {
  hint?: string;
  label: string;
  name: string;
  onChange: (value: string) => void;
  placeholder?: string;
  value: string;
  wide?: boolean;
}

const Field = ({ hint, label, name, onChange, placeholder, value, wide }: FieldProps) => (
  <div className={wide ? 'tw-flex-1' : 'tw-w-[280px]'}>
    <Input
      className={wide ? 'tw-max-w-full' : undefined}
      label={label}
      LabelProps={{ className: LABEL_CLASSES }}
      name={name}
      placeholder={placeholder}
      type="text"
      value={value}
      onChange={event => onChange(event.target.value)}
    />
    {hint && <Text className="tw-text-very-dark-grey tw-mt-1" size="sm">{hint}</Text>}
  </div>
);

interface LeveledReadingRuleFormProps {
  createUrl: string;
  initialKind?: string;
  initialNotes?: string;
  initialTerm?: string;
  kinds: string[];
}

const humanize = (kind: string) => {
  const spaced = kind.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const LeveledReadingRuleForm = ({
  createUrl,
  initialKind,
  initialNotes,
  initialTerm,
  kinds,
}: LeveledReadingRuleFormProps) => {
  const [allowedExample, setAllowedExample] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [exampleViolation, setExampleViolation] = useState('');
  const [kind, setKind] = useState(initialKind && kinds.includes(initialKind) ? initialKind : kinds[0]);
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState(initialNotes || '');
  const [pattern, setPattern] = useState('');
  const [saving, setSaving] = useState(false);
  const [term, setTerm] = useState(initialTerm || '');
  // Prefilled arrivals (the dashboard's "Add content rule…" link) land ready to type.
  const [isOpen, setIsOpen] = useState(Boolean(initialKind || initialNotes || initialTerm));

  const matcher = buildMatcher(term, pattern);
  const patternInvalid = pattern.trim().length > 0 && matcher === null;
  const kindOptions = kinds.map(k => ({ label: humanize(k), value: k }));

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setupCsrfToken();
    setSaving(true);
    setErrors([]);
    Axios.post(createUrl, {
      leveled_reading_content_rule: {
        allowed_example: allowedExample,
        example_violation: exampleViolation,
        kind,
        label,
        notes,
        pattern,
        term,
      },
    })
      .then(() => window.location.reload())
      .catch((error) => {
        setErrors(error.response?.data?.errors || ['Failed to save the rule.']);
        setSaving(false);
      });
  };

  return (
    <>
      <Button onClick={() => setIsOpen(true)}>Add a rule</Button>
      <Modal close={() => setIsOpen(false)} headerText="Add a content rule" isOpen={isOpen}>
        <Text className="tw-text-very-dark-grey tw-mb-4" size="sm">
          The preview tests your matcher live against the examples as you type; the authoritative
          validation runs on save. New rules apply to the next generation immediately.
        </Text>
        <form onSubmit={submit}>
          {errors.length > 0 && (
            <Text className="tw-mb-3" size="sm" style={{ color: '#d03b3b' }}>{errors.join('. ')}</Text>
          )}
          <div className="tw-flex tw-flex-wrap tw-gap-4 tw-mb-4">
            <div className="tw-flex tw-flex-col tw-gap-2 tw-w-[280px]">
              <Label className={LABEL_CLASSES} htmlFor="lr-rule-kind">Kind</Label>
              <Select
                inputId="lr-rule-kind"
                options={kindOptions}
                value={kindOptions.find(option => option.value === kind)}
                onChange={option => setKind((option as { value: string }).value)}
              />
            </div>
            <Field
              label="Label"
              name="lr_rule_label"
              placeholder='e.g. "forced relocation"'
              value={label}
              onChange={setLabel}
            />
          </div>
          <div className="tw-flex tw-flex-wrap tw-gap-4 tw-mb-4">
            <Field
              hint="Matched as a whole word/phrase, plural allowed. Use this for almost every rule."
              label="Term"
              name="lr_rule_term"
              placeholder="plain word or phrase"
              value={term}
              onChange={setTerm}
            />
            <Field
              hint="Only for compound cases. Validated against the examples on save."
              label="Pattern (advanced)"
              name="lr_rule_pattern"
              placeholder="regex — leave blank if term is set"
              value={pattern}
              onChange={setPattern}
            />
          </div>
          <div className="tw-flex tw-flex-wrap tw-gap-4 tw-mb-1">
            <Field
              label="Example violation (must match)"
              name="lr_rule_example_violation"
              value={exampleViolation}
              onChange={setExampleViolation}
            />
            <Field
              label="Allowed example (must not match)"
              name="lr_rule_allowed_example"
              value={allowedExample}
              onChange={setAllowedExample}
            />
          </div>
          <div aria-live="polite" className="tw-mb-4 tw-min-h-[18px]">
            {patternInvalid && (
              <div className="tw-text-sm" style={{ color: '#8f1f1f' }}>✗ Pattern is not a valid expression</div>
            )}
            <PreviewLine expected label="Example violation" matcher={matcher} text={exampleViolation} />
            <PreviewLine expected={false} label="Allowed example" matcher={matcher} text={allowedExample} />
          </div>
          <div className="tw-mb-4">
            <Field
              wide
              label="Notes (why — link the trace or ticket that prompted it)"
              name="lr_rule_notes"
              value={notes}
              onChange={setNotes}
            />
          </div>
          <SubmitButton submitting={saving} submittingText="Saving…">Add rule</SubmitButton>
        </form>
      </Modal>
    </>
  );
};

export default LeveledReadingRuleForm;
